import { useEffect, useMemo, useState } from 'react';
import { AdminUser, Comment } from '../../pm/types';
import { createComment, subscribeTaskComments } from '../../pm/firebase';
import CommentComposer from './CommentComposer';
import CommentNode from './CommentNode';

interface Props {
  taskId: string;
  projectId: string;
  admins: AdminUser[];
  currentUid: string;
  onCountChange?: (count: number) => void;
}

const CommentThread: React.FC<Props> = ({ taskId, projectId, admins, currentUid, onCountChange }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const unsub = subscribeTaskComments(taskId, (items) => {
      setComments(items);
      setLoaded(true);
      if (onCountChange) onCountChange(items.length);
    });
    return unsub;
  }, [taskId, onCountChange]);

  const childrenByParent = useMemo(() => {
    const m: Record<string, Comment[]> = {};
    for (const c of comments) {
      const key = c.parentId || '';
      if (!m[key]) m[key] = [];
      m[key].push(c);
    }
    const ids = new Set(comments.map((c) => c.id));
    const orphans: Comment[] = [];
    for (const [parentId, group] of Object.entries(m)) {
      if (parentId && !ids.has(parentId)) {
        orphans.push(...group);
        delete m[parentId];
      }
    }
    if (orphans.length) {
      if (!m['']) m[''] = [];
      m[''].push(...orphans);
      m[''].sort((a, b) => a.createdAt - b.createdAt);
    }
    return m;
  }, [comments]);

  const topLevel = childrenByParent[''] || [];

  async function handleTopLevelSubmit(body: string, mentions: string[]) {
    await createComment(
      { taskId, projectId, parentId: '', body, mentions },
      currentUid,
    );
  }

  return (
    <div className="pm-comment-thread">
      <CommentComposer
        admins={admins}
        placeholder="Add a comment…"
        submitLabel="Post"
        onSubmit={handleTopLevelSubmit}
      />
      {!loaded ? (
        <div className="pm-comment-empty">Loading…</div>
      ) : topLevel.length === 0 ? (
        <div className="pm-comment-empty">No comments yet. Start the thread above.</div>
      ) : (
        <div className="pm-comment-list">
          {topLevel.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              allComments={comments}
              childrenByParent={childrenByParent}
              admins={admins}
              currentUid={currentUid}
              projectId={projectId}
              depth={0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentThread;
